import { useState, useEffect, useRef } from 'react';
import {
  X, Plus, Trash2, Key, Code, Tag, ChevronUp, ChevronDown, ChevronRight,
  GripVertical, Library, Hash, Database, ArrowRight, ArrowLeft,
  Link2, Save, Layers, Edit2, Check, AlertCircle, Info, Split,
  Lock, Unlock,
} from 'lucide-react';
import { DATA_TYPES, NODE_TYPES } from '../../utils/constants';
import { createDefaultField } from '../../utils/helpers';
import { useConfirm } from '../../hooks/useConfirm';
import { domainApi } from '../../utils/api';
import ConfirmDialog from './ConfirmDialog';
import SaveDomainModal from './SaveDomainModal';
import toast from 'react-hot-toast';
import useFlowStore from '../../stores/useFlowStore';

const TAG_COLORS = [
  { name: 'Red',     value: '#EF4444', }, { name: 'Orange',  value: '#F97316' },
  { name: 'Amber',   value: '#F59E0B', }, { name: 'Yellow',  value: '#EAB308' },
  { name: 'Lime',    value: '#84CC16', }, { name: 'Green',   value: '#10B981' },
  { name: 'Emerald', value: '#059669', }, { name: 'Teal',    value: '#14B8A6' },
  { name: 'Cyan',    value: '#06B6D4', }, { name: 'Sky',     value: '#0EA5E9' },
  { name: 'Blue',    value: '#3B82F6', }, { name: 'Indigo',  value: '#6366F1' },
  { name: 'Violet',  value: '#8B5CF6', }, { name: 'Purple',  value: '#A855F7' },
  { name: 'Fuchsia', value: '#D946EF', }, { name: 'Pink',    value: '#EC4899' },
  { name: 'Rose',    value: '#F43F5E', }, { name: 'Gray',    value: '#6B7280' },
];

const EntityEditModal = ({
  isOpen,
  onClose,
  selectedNode,
  initialData,
  modelType,
  globalTags,
  addGlobalTag,
  globalDomains = [],
  addGlobalDomain,
  edges,
  nodes,
  userTypes,
  userHasRole,
  onSave,
  onSelectEdge,
}) => {
  const { isOpen: confirmOpen, config: confirmConfig, confirm, cancel } = useConfirm();
  const [activeTab, setActiveTab]             = useState('entity');
  const [localData, setLocalData]             = useState(null);
  const [showSaveDomainModal, setShowSaveDomainModal] = useState(false);
  const [saveDomainData, setSaveDomainData]   = useState(null);
  const [showNewTagForm, setShowNewTagForm]   = useState(false);
  const [newTagText, setNewTagText]           = useState('');
  const [newTagColor, setNewTagColor]         = useState(TAG_COLORS[0].value);
  const [showNewDomainForm, setShowNewDomainForm] = useState(false);
  const [newDomainText, setNewDomainText]     = useState('');
  const [draggedFieldIndex, setDraggedFieldIndex] = useState(null);
  const [dragOverFieldIndex, setDragOverFieldIndex] = useState(null);

  // Partition form state
  const [partitionForm, setPartitionForm]     = useState({ id: null, name: '', from: '', to: '', values: '' });
  const [isEditingPartition, setIsEditingPartition] = useState(false);

  // Auto-focus ref for new fields
  const newFieldInputRef = useRef(null);
  const [shouldFocusNewField, setShouldFocusNewField] = useState(false);

  const availableSchemas = Array.from(new Set((nodes || []).map(n => n.data?.schema).filter(Boolean))).sort();

  const handleRenameSchemaGlobal = (oldSchema) => {
    if (!oldSchema) return;
    const newSchema = window.prompt(`Rename schema "${oldSchema}" for all entities:`, oldSchema);
    if (newSchema !== null && newSchema.trim() !== '' && newSchema.trim() !== oldSchema) {
      const trimmedNew = newSchema.trim();
      const store = useFlowStore.getState?.();
      if (store && store.renameSchema) {
        store.renameSchema(oldSchema, trimmedNew);
      }
      setLocalData(prev => prev ? { ...prev, schema: trimmedNew } : prev);
      toast.success(`Schema "${oldSchema}" renamed to "${trimmedNew}" across all entities!`);
    }
  };

  // Initialise local copy whenever the modal opens
  useEffect(() => {
    if (isOpen && initialData) {
      setLocalData(JSON.parse(JSON.stringify(initialData)));
      setActiveTab('entity');
      setShowNewTagForm(false);
      setNewTagText('');
      setNewTagColor(TAG_COLORS[0].value);
      setShowNewDomainForm(false);
      setNewDomainText('');
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (shouldFocusNewField && newFieldInputRef.current) {
      newFieldInputRef.current.focus();
      newFieldInputRef.current.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
      setShouldFocusNewField(false);
    }
  }, [localData?.fields, shouldFocusNewField]);

  if (!isOpen || !localData || !selectedNode) return null;

  const isTextNode = selectedNode.type === NODE_TYPES.TEXT;
  const isViewNode = selectedNode.type === NODE_TYPES.VIEW;

  // ── Helpers ────────────────────────────────────────────────────────────────

  const hasActiveRelationships = () =>
    edges?.some(e => e.source === selectedNode.id || e.target === selectedNode.id) ?? false;

  const getDataTypes = () => {
    const base = DATA_TYPES[modelType?.toUpperCase()] || DATA_TYPES.RELATIONAL;
    const udts = (userTypes || []).map(u => `UDT:${u.name}`);
    return udts.length > 0 ? [...base, ...udts] : base;
  };

  const getNodeRelationships = () => {
    if (!selectedNode || !edges) return { outgoing: [], incoming: [] };
    const outgoing = edges
      .filter(e => e.source === selectedNode.id)
      .map(e => ({ ...e, targetNode: nodes.find(n => n.id === e.target) }));
    const incoming = edges
      .filter(e => e.target === selectedNode.id)
      .map(e => ({ ...e, sourceNode: nodes.find(n => n.id === e.source) }));
    return { outgoing, incoming };
  };

  // ── Partitioning Helpers & Lock Detection ───────────────────────────────────

  const partitioning = localData?.partitioning;
  const isPartitionActive = partitioning && partitioning.type && partitioning.type !== 'none';
  const partitionLockedColumnNames = isPartitionActive
    ? (partitioning.type === 'hash'
        ? (partitioning.columns || [])
        : (partitioning.column ? [partitioning.column] : (partitioning.columns || [])))
    : [];

  const getAllEligiblePartitionFields = () => {
    const regularFields = (localData?.fields || []).map(f => ({
      name: f.name,
      type: f.type || 'VARCHAR',
      isForeignKey: !!(f.isForeignKey || f.foreignKey || f.isFk),
      isPrimaryKey: !!f.isPrimaryKey,
      isVirtual: false,
    }));

    // Also include foreign keys from incoming relationships
    const { incoming } = getNodeRelationships();
    const existingNames = new Set(regularFields.map(f => (f.name || '').toLowerCase()));

    incoming.forEach(rel => {
      if (rel.data?.cardinality !== 'N:N') {
        const srcName = rel.sourceNode?.data?.name || 'entity';
        const fkName = (rel.data?.fkAlias && String(rel.data.fkAlias).trim()) || `${srcName.toLowerCase()}_id`;
        if (!existingNames.has(fkName.toLowerCase())) {
          existingNames.add(fkName.toLowerCase());
          regularFields.push({
            name: fkName,
            type: 'INTEGER (FK)',
            isForeignKey: true,
            isPrimaryKey: false,
            isVirtual: true,
            sourceName: srcName,
          });
        }
      }
    });

    return regularFields;
  };

  // ── Field operations ────────────────────────────────────────────────────────

  const addField = () => {
    const hasPK = (localData.fields || []).some(f => f.isPrimaryKey);
    const newField = createDefaultField('', 'VARCHAR', !hasPK && (localData.fields || []).length === 0);
    setLocalData({ ...localData, fields: [...(localData.fields || []), newField] });
    setShouldFocusNewField(true);
  };

  const updateField = async (index, field) => {
    const oldField = (localData.fields || [])[index];

    // Check if field is locked by active partitioning
    if (oldField && partitionLockedColumnNames.includes(oldField.name)) {
      if (field.name !== oldField.name || field.type !== oldField.type) {
        toast.error(`Field "${oldField.name}" is locked by ${partitioning?.type?.toUpperCase()} partitioning. Remove or change the partition before modifying it.`);
        return;
      }
    }

    if (oldField.isPrimaryKey && !field.isPrimaryKey) {
      if (hasActiveRelationships()) {
        await confirm({
          title: 'Cannot Remove Primary Key',
          message: `This Primary Key is being used in active relationships.\n\nDelete all relationships connected to "${localData.name}" before removing the Primary Key.`,
          confirmText: 'OK', cancelText: null, type: 'warning', onConfirm: () => {},
        });
        return;
      }
      const pkCount = (localData.fields || []).filter(f => f.isPrimaryKey).length;
      if (pkCount === 1 && hasActiveRelationships()) {
        await confirm({
          title: 'Cannot Remove Last Primary Key',
          message: `This is the only Primary Key in "${localData.name}" and it has active relationships.`,
          confirmText: 'OK', cancelText: null, type: 'warning', onConfirm: () => {},
        });
        return;
      }
    }

    const newFields = [...(localData.fields || [])];
    newFields[index] = field;
    setLocalData({ ...localData, fields: newFields });
  };

  const deleteField = async (index) => {
    const field = (localData.fields || [])[index];

    // Check if field is locked by active partitioning
    if (field && partitionLockedColumnNames.includes(field.name)) {
      await confirm({
        title: 'Field Locked by Partitioning',
        message: `The field "${field.name}" is currently used as a partition key for this table (${partitioning?.type?.toUpperCase()}).\n\nTo delete or modify this field, please remove or change the partitioning configuration in the "Partitions" tab first.`,
        confirmText: 'OK', cancelText: null, type: 'warning', onConfirm: () => {},
      });
      return;
    }

    if (field?.isPrimaryKey) {
      if (hasActiveRelationships()) {
        await confirm({
          title: 'Cannot Delete Primary Key',
          message: `This Primary Key is being used in active relationships. Delete all relationships first.`,
          confirmText: 'OK', cancelText: null, type: 'warning', onConfirm: () => {},
        });
        return;
      }
      const pkCount = (localData.fields || []).filter(f => f.isPrimaryKey).length;
      if (pkCount === 1) {
        await confirm({
          title: 'Delete Last Primary Key',
          message: `You are about to delete the only Primary Key in "${localData.name}".\n\nWithout a Primary Key you will not be able to create relationships.`,
          confirmText: 'Delete Anyway', cancelText: 'Cancel', type: 'warning',
          onConfirm: () => {
            setLocalData({ ...localData, fields: (localData.fields || []).filter((_, i) => i !== index) });
          },
        });
        return;
      }
    }

    await confirm({
      title: 'Delete Field',
      message: `Are you sure you want to delete field "${field?.name || 'this field'}"?`,
      confirmText: 'Delete', cancelText: 'Cancel', type: 'danger',
      onConfirm: () => {
        setLocalData({ ...localData, fields: (localData.fields || []).filter((_, i) => i !== index) });
      },
    });
  };

  const handleDragStart = (e, i) => { setDraggedFieldIndex(i); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver  = (e, i) => { e.preventDefault(); if (draggedFieldIndex !== i) setDragOverFieldIndex(i); };
  const handleDragLeave = ()      => setDragOverFieldIndex(null);
  const handleDragEnd   = ()      => { setDraggedFieldIndex(null); setDragOverFieldIndex(null); };

  const handleDrop = (e, dropIdx) => {
    e.preventDefault();
    if (draggedFieldIndex === null || draggedFieldIndex === dropIdx) {
      setDraggedFieldIndex(null); setDragOverFieldIndex(null); return;
    }
    const arr = [...(localData.fields || [])];
    const [moved] = arr.splice(draggedFieldIndex, 1);
    arr.splice(dropIdx, 0, moved);
    setLocalData({ ...localData, fields: arr });
    setDraggedFieldIndex(null); setDragOverFieldIndex(null);
  };

  // ── Tag operations ──────────────────────────────────────────────────────────

  const addTagToEntity = (tag) => {
    const entityTag = { id: `${Date.now()}_${Math.random()}`, text: tag.text, color: tag.color, globalTagId: tag.id };
    const updatedTags = [...(localData.tags || []), entityTag];
    setLocalData({ ...localData, tags: updatedTags, color: updatedTags.length === 1 ? entityTag.color : localData.color });
  };

  const createAndAddNewTag = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (!newTagText.trim()) return;
    const globalTag = { id: Date.now().toString(), text: newTagText.trim(), color: newTagColor };
    addGlobalTag(globalTag);
    addTagToEntity(globalTag);
    setNewTagText(''); setNewTagColor(TAG_COLORS[0].value); setShowNewTagForm(false);
  };

  const updateTag = (tagId, updates) => {
    const newTags = (localData.tags || []).map(t => t.id === tagId ? { ...t, ...updates } : t);
    setLocalData({ ...localData, tags: newTags, color: newTags[0]?.color ?? null });
  };

  const deleteTag = (tagId) => {
    const newTags = (localData.tags || []).filter(t => t.id !== tagId);
    setLocalData({ ...localData, tags: newTags, color: newTags[0]?.color ?? null });
  };

  const moveTagUp = (i) => {
    if (i === 0) return;
    const arr = [...(localData.tags || [])];
    [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
    setLocalData({ ...localData, tags: arr, color: arr[0]?.color ?? null });
  };

  const moveTagDown = (i) => {
    const arr = [...(localData.tags || [])];
    if (i >= arr.length - 1) return;
    [arr[i], arr[i + 1]] = [arr[i + 1], arr[i]];
    setLocalData({ ...localData, tags: arr, color: arr[0]?.color ?? null });
  };

  // ── Entity Domain operations ────────────────────────────────────────────────

  const addDomainToEntity = (globalDomain) => {
    const alreadyAdded = (localData.domains || []).some(d => d.globalDomainId === globalDomain.id);
    if (alreadyAdded) return;
    const entityDomain = {
      id: `${Date.now()}_${Math.random()}`,
      text: globalDomain.text,
      globalDomainId: globalDomain.id,
    };
    setLocalData({ ...localData, domains: [...(localData.domains || []), entityDomain] });
  };

  const createAndAddNewDomain = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (!newDomainText.trim()) return;
    const globalDomain = { id: Date.now().toString(), text: newDomainText.trim() };
    if (addGlobalDomain) addGlobalDomain(globalDomain);
    addDomainToEntity(globalDomain);
    setNewDomainText('');
    setShowNewDomainForm(false);
  };

  const removeDomainFromEntity = (domainId) => {
    setLocalData({ ...localData, domains: (localData.domains || []).filter(d => d.id !== domainId) });
  };

  // ── Objects Library operations ───────────────────────────────────────────────

  const handleSaveDomainField = (field) => { setSaveDomainData({ field }); setShowSaveDomainModal(true); };

  const handleConfirmSaveDomain = async (domainData) => {
    try {
      await domainApi.create(domainData);
      toast.success('Object saved successfully!');
      setShowSaveDomainModal(false); setSaveDomainData(null);
    } catch (err) {
      toast.error(err.message || 'Failed to save object');
    }
  };

  // ── Partition operations ──────────────────────────────────────────────────
  const currentPartitioning = localData?.partitioning || {
    type: 'none',
    column: localData?.fields?.[0]?.name || '',
    columns: [],
    partitions: [],
    hashPartitionsCount: 4
  };

  const handleSetPartitionType = (type) => {
    const defaultCol = localData?.fields?.[0]?.name || '';
    setLocalData({
      ...localData,
      partitioning: {
        type,
        column: currentPartitioning.column || defaultCol,
        columns: currentPartitioning.columns?.length ? currentPartitioning.columns : (defaultCol ? [defaultCol] : []),
        partitions: currentPartitioning.type === type ? (currentPartitioning.partitions || []) : [],
        hashPartitionsCount: currentPartitioning.hashPartitionsCount || 4,
      }
    });
    setPartitionForm({ id: null, name: '', from: '', to: '', values: '' });
    setIsEditingPartition(false);
  };

  const handleSetPartitionColumn = (colName) => {
    setLocalData({
      ...localData,
      partitioning: {
        ...currentPartitioning,
        column: colName,
        columns: [colName],
      }
    });
  };

  const handleToggleHashColumn = (colName) => {
    const curCols = currentPartitioning.columns || [];
    const newCols = curCols.includes(colName)
      ? curCols.filter(c => c !== colName)
      : [...curCols, colName];
    setLocalData({
      ...localData,
      partitioning: {
        ...currentPartitioning,
        columns: newCols,
      }
    });
  };

  const handleSetHashPartitionsCount = (count) => {
    const val = Math.max(1, parseInt(count, 10) || 1);
    setLocalData({
      ...localData,
      partitioning: {
        ...currentPartitioning,
        hashPartitionsCount: val,
      }
    });
  };

  const handleSaveRangePartition = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    const fromVal = partitionForm.from.trim();
    const toVal = partitionForm.to.trim();

    if (!fromVal && !toVal) {
      toast.error('Informe pelo menos o início ou o fim do intervalo.');
      return;
    }

    const partitions = [...(currentPartitioning.partitions || [])];
    const partName = partitionForm.name.trim() || `p_${partitions.length + 1}`;

    if (isEditingPartition && partitionForm.id) {
      const idx = partitions.findIndex(p => p.id === partitionForm.id);
      if (idx !== -1) {
        partitions[idx] = {
          ...partitions[idx],
          name: partName,
          from: fromVal,
          to: toVal,
        };
      }
    } else {
      partitions.push({
        id: `${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
        name: partName,
        from: fromVal,
        to: toVal,
      });
    }

    setLocalData({
      ...localData,
      partitioning: {
        ...currentPartitioning,
        partitions,
      }
    });

    setPartitionForm({ id: null, name: '', from: '', to: '', values: '' });
    setIsEditingPartition(false);
    toast.success(isEditingPartition ? 'Partition updated successfully!' : 'Partition added successfully!');
  };

  const handleSaveListPartition = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    const partitions = [...(currentPartitioning.partitions || [])];
    const partName = partitionForm.name.trim() || `p_${partitions.length + 1}`;
    const valuesVal = partitionForm.values.trim();

    if (isEditingPartition && partitionForm.id) {
      const idx = partitions.findIndex(p => p.id === partitionForm.id);
      if (idx !== -1) {
        partitions[idx] = {
          ...partitions[idx],
          name: partName,
          values: valuesVal,
        };
      }
    } else {
      partitions.push({
        id: `${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
        name: partName,
        values: valuesVal,
      });
    }

    setLocalData({
      ...localData,
      partitioning: {
        ...currentPartitioning,
        partitions,
      }
    });

    setPartitionForm({ id: null, name: '', from: '', to: '', values: '' });
    setIsEditingPartition(false);
    toast.success(isEditingPartition ? 'Partition updated successfully!' : 'Partition added successfully!');
  };

  const handleEditPartition = (partition) => {
    setPartitionForm({
      id: partition.id,
      name: partition.name || '',
      from: partition.from || '',
      to: partition.to || '',
      values: partition.values || ''
    });
    setIsEditingPartition(true);
  };

  const handleDeletePartition = (partitionId) => {
    const partitions = (currentPartitioning.partitions || []).filter(p => p.id !== partitionId);
    setLocalData({
      ...localData,
      partitioning: {
        ...currentPartitioning,
        partitions,
      }
    });
    if (partitionForm.id === partitionId) {
      setPartitionForm({ id: null, name: '', from: '', to: '', values: '' });
      setIsEditingPartition(false);
    }
    toast.success('Partition removed successfully.');
  };

  const handleCancelPartitionEdit = () => {
    setPartitionForm({ id: null, name: '', from: '', to: '', values: '' });
    setIsEditingPartition(false);
  };

  // ── Main save ───────────────────────────────────────────────────────────────

  const handleSave = () => { onSave(localData); onClose(); };

  // ── Tab definitions ─────────────────────────────────────────────────────────

  const { outgoing, incoming } = getNodeRelationships();
  const tagCount  = (localData.tags   || []).length;
  const fieldCount = (localData.fields || []).length;
  const relCount  = outgoing.length + incoming.length;

  const partitionBadgeCount = isPartitionActive
    ? (partitioning.type === 'hash'
        ? (partitioning.columns?.length || 1)
        : (partitioning.partitions?.length || 1))
    : undefined;

  const tabs = [
    { id: 'entity', label: 'Entity' },
    ...(isTextNode ? [] : [
      { id: 'tags',          label: 'Tags',          count: tagCount   },
      { id: 'fields',        label: 'Fields',        count: fieldCount },
      { id: 'partitions',    label: 'Partitions',    count: partitionBadgeCount },
      { id: 'relationships', label: 'Relationships', count: relCount   },
    ]),
  ];

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Backdrop — click does NOT close to prevent accidental data loss */}
      <div className="fixed inset-0 bg-black bg-opacity-60 z-[60] flex items-center justify-center p-4">

        {/* Dialog */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-3xl flex flex-col max-h-[90vh]">

          {/* ── Header ── */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              <span className="flex-shrink-0 text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-md font-mono capitalize">
                {selectedNode.type}
              </span>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                {localData.name || 'Unnamed Entity'}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="flex-shrink-0 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-500 dark:text-gray-400 transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* ── Tabs ── */}
          <div className="flex border-b border-gray-200 dark:border-gray-700 px-6 flex-shrink-0 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                {tab.label}
                {tab.count !== undefined && tab.count > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    activeTab === tab.id
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* ── Tab content ── */}
          <div className="flex-1 overflow-y-auto p-6 min-h-0">

            {/* ═══════════════════ ENTITY TAB ═══════════════════ */}
            {activeTab === 'entity' && (
              <div className="space-y-6">

                {/* Schema */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Schema (optional)
                    </label>
                    {localData.schema && (
                      <button
                        type="button"
                        onClick={() => handleRenameSchemaGlobal(localData.schema)}
                        className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 underline font-normal"
                        title="Rename schema across all entities"
                      >
                        Rename globally
                      </button>
                    )}
                  </div>
                  <input
                    type="text"
                    list="existing-schemas-modal"
                    value={localData.schema || ''}
                    onChange={e => setLocalData({ ...localData, schema: e.target.value })}
                    placeholder="e.g. public, sales, dbo"
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-base"
                  />
                  <datalist id="existing-schemas-modal">
                    {availableSchemas.map((sch) => (
                      <option key={sch} value={sch} />
                    ))}
                  </datalist>
                </div>

                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Entity Name
                  </label>
                  <input
                    type="text"
                    value={localData.name || ''}
                    onChange={e => setLocalData({ ...localData, name: e.target.value })}
                    autoFocus
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-base"
                  />
                </div>

                {/* Description (not TEXT) */}
                {!isTextNode && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Description
                    </label>
                    <textarea
                      value={localData.description || ''}
                      onChange={e => setLocalData({ ...localData, description: e.target.value })}
                      rows={4}
                      placeholder="Describe this entity…"
                      className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                    />
                  </div>
                )}

                {/* Domain (not TEXT) */}
                {!isTextNode && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Domain
                    </label>

                    {/* Assigned domains */}
                    {(localData.domains || []).length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {(localData.domains || []).map(domain => (
                          <span
                            key={domain.id}
                            className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300 rounded-md text-sm"
                          >
                            <span className="italic">{domain.text}</span>
                            <button
                              type="button"
                              onClick={() => removeDomainFromEntity(domain.id)}
                              className="text-indigo-500 hover:text-indigo-700 dark:hover:text-indigo-200 ml-0.5"
                              title="Remove"
                            >
                              <X size={12} />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Available domains from global library */}
                    {globalDomains.filter(gd => !(localData.domains || []).some(d => d.globalDomainId === gd.id)).length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">Available domains:</p>
                        <div className="flex flex-wrap gap-1.5">
                          {globalDomains
                            .filter(gd => !(localData.domains || []).some(d => d.globalDomainId === gd.id))
                            .map(gd => (
                              <button
                                key={gd.id}
                                type="button"
                                onClick={() => addDomainToEntity(gd)}
                                className="px-2 py-1 text-xs rounded-md border border-indigo-300 dark:border-indigo-600 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors flex items-center gap-1"
                              >
                                <Plus size={11} />
                                {gd.text}
                              </button>
                            ))}
                        </div>
                      </div>
                    )}

                    {/* Create new domain */}
                    {showNewDomainForm ? (
                      <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg space-y-2">
                        <input
                          type="text"
                          value={newDomainText}
                          onChange={e => setNewDomainText(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); createAndAddNewDomain(e); } }}
                          placeholder="Domain name…"
                          autoFocus
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                        />
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={createAndAddNewDomain}
                            disabled={!newDomainText.trim()}
                            className="flex-1 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Create & Add
                          </button>
                          <button
                            type="button"
                            onClick={() => { setShowNewDomainForm(false); setNewDomainText(''); }}
                            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setShowNewDomainForm(true)}
                        className="w-full px-3 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:border-indigo-500 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors flex items-center justify-center gap-2"
                      >
                        <Plus size={15} />
                        {(localData.domains || []).length === 0 && globalDomains.length === 0
                          ? 'Create First Domain'
                          : 'Add Domain'}
                      </button>
                    )}
                  </div>
                )}

                {/* Materialized View (VIEW only) */}
                {isViewNode && (
                  <div className="flex items-center gap-3 p-4 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg border border-cyan-200 dark:border-cyan-800">
                    <input
                      type="checkbox"
                      id="modal-materialized"
                      checked={localData.materialized || false}
                      onChange={e => setLocalData({ ...localData, materialized: e.target.checked })}
                      className="w-4 h-4 rounded text-cyan-600 focus:ring-cyan-500"
                    />
                    <label htmlFor="modal-materialized" className="flex items-center gap-2 text-sm font-medium text-cyan-900 dark:text-cyan-100 cursor-pointer">
                      <Database size={16} />
                      Materialized View
                    </label>
                    <span className="text-xs text-cyan-700 dark:text-cyan-300 ml-auto">
                      {localData.materialized ? '✓ Materialized' : 'Standard'}
                    </span>
                  </div>
                )}

                {/* SQL Definition (VIEW only) */}
                {isViewNode && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                      <Code size={14} /> SQL Definition
                    </label>
                    <textarea
                      value={localData.sqlDefinition || ''}
                      onChange={e => setLocalData({ ...localData, sqlDefinition: e.target.value })}
                      rows={10}
                      placeholder={'SELECT column1, column2\nFROM table1\nWHERE condition'}
                      className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none font-mono text-sm"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Define the SQL SELECT statement for this view
                    </p>
                  </div>
                )}

                {/* TEXT node fields */}
                {isTextNode && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Content (Markdown)
                      </label>
                      <textarea
                        value={localData.content || ''}
                        onChange={e => setLocalData({ ...localData, content: e.target.value })}
                        rows={20}
                        placeholder={'# Heading\n\nWrite your notes here using **Markdown**!'}
                        className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none font-mono text-sm"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Supports Markdown: headings, bold, italic, lists, links, code, tables…
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Background Color
                      </label>
                      <div className="flex gap-3 flex-wrap">
                        {[
                          { color: '#FEF9C3', label: 'Yellow' }, { color: '#DBEAFE', label: 'Blue'   },
                          { color: '#D1FAE5', label: 'Green'  }, { color: '#FCE7F3', label: 'Pink'   },
                          { color: '#E0E7FF', label: 'Indigo' }, { color: '#FEE2E2', label: 'Red'    },
                          { color: '#F3F4F6', label: 'Gray'   },
                        ].map(({ color, label }) => (
                          <button
                            key={color}
                            onClick={() => setLocalData({ ...localData, backgroundColor: color })}
                            className={`w-12 h-12 rounded-lg border-2 transition-all ${
                              localData.backgroundColor === color
                                ? 'border-blue-600 scale-110 shadow-md'
                                : 'border-gray-300 hover:scale-105'
                            }`}
                            style={{ backgroundColor: color }}
                            title={label}
                          />
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Width: {localData.width || 300}px
                      </label>
                      <input
                        type="range" min="200" max="600" step="50"
                        value={localData.width || 300}
                        onChange={e => setLocalData({ ...localData, width: parseInt(e.target.value) })}
                        className="w-full"
                      />
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ═══════════════════ TAGS TAB ═══════════════════ */}
            {activeTab === 'tags' && (
              <div className="space-y-8">

                {/* Current entity tags */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
                    <Tag size={14} /> Entity Tags
                    {tagCount > 0 && (
                      <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full">
                        {tagCount}
                      </span>
                    )}
                  </h3>

                  {tagCount === 0 ? (
                    <p className="text-sm text-gray-400 italic">No tags assigned yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {(localData.tags || []).map((tag, index) => (
                        <div
                          key={tag.id}
                          className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600"
                        >
                          <div className="flex items-center gap-3 mb-3">
                            <div className="flex flex-col gap-1">
                              <button type="button" onClick={() => moveTagUp(index)} disabled={index === 0}
                                className="p-1 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-600 rounded disabled:opacity-30">
                                <ChevronUp size={14} />
                              </button>
                              <button type="button" onClick={() => moveTagDown(index)} disabled={index === tagCount - 1}
                                className="p-1 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-600 rounded disabled:opacity-30">
                                <ChevronDown size={14} />
                              </button>
                            </div>

                            <div className="w-8 h-8 rounded-full flex-shrink-0 border-2 border-white dark:border-gray-600 shadow-sm"
                              style={{ backgroundColor: tag.color }} />

                            <input
                              type="text"
                              value={tag.text || ''}
                              onChange={e => updateTag(tag.id, { text: e.target.value })}
                              placeholder="Tag text…"
                              className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />

                            <button type="button" onClick={() => deleteTag(tag.id)}
                              className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors">
                              <Trash2 size={16} />
                            </button>
                          </div>

                          <div className="ml-12">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Color:</p>
                            <div className="flex flex-wrap gap-2">
                              {TAG_COLORS.map(c => (
                                <button key={c.value} type="button" onClick={() => updateTag(tag.id, { color: c.value })}
                                  className={`w-7 h-7 rounded-full border-2 transition-all ${
                                    tag.color === c.value ? 'border-gray-900 dark:border-white scale-110' : 'border-transparent hover:scale-105'
                                  }`}
                                  style={{ backgroundColor: c.value }} title={c.name} />
                              ))}
                            </div>
                          </div>

                          {index === 0 && (
                            <p className="text-xs text-blue-600 dark:text-blue-400 mt-2 ml-12">
                              ★ This tag color is applied to the entity header
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Available tags */}
                {globalTags.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                      Available Tags
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {globalTags.map(tag => {
                        const added = (localData.tags || []).some(t => t.globalTagId === tag.id);
                        return (
                          <button
                            key={tag.id}
                            type="button"
                            onClick={() => !added && addTagToEntity(tag)}
                            disabled={added}
                            className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded-full text-white font-medium transition-all ${
                              added ? 'opacity-40 cursor-not-allowed' : 'hover:scale-105 active:scale-95'
                            }`}
                            style={{ backgroundColor: tag.color }}
                            title={added ? 'Already added' : 'Click to add'}
                          >
                            {!added && <Plus size={13} />}
                            {tag.text}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Create new tag */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                    Create New Tag
                  </h3>
                  {showNewTagForm ? (
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl space-y-3">
                      <input
                        type="text" value={newTagText} onChange={e => setNewTagText(e.target.value)}
                        placeholder="Tag text…" autoFocus
                        onKeyDown={e => e.key === 'Enter' && createAndAddNewTag(e)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <div className="flex flex-wrap gap-2">
                        {TAG_COLORS.map(c => (
                          <button key={c.value} type="button" onClick={() => setNewTagColor(c.value)}
                            className={`w-7 h-7 rounded-full border-2 transition-all ${
                              newTagColor === c.value ? 'border-gray-900 dark:border-white scale-110' : 'border-transparent hover:scale-105'
                            }`}
                            style={{ backgroundColor: c.value }} title={c.name} />
                        ))}
                      </div>
                      <div className="flex gap-3">
                        <button type="button" onClick={createAndAddNewTag} disabled={!newTagText.trim()}
                          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
                          Create &amp; Add
                        </button>
                        <button type="button" onClick={() => { setShowNewTagForm(false); setNewTagText(''); }}
                          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300">
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button type="button" onClick={() => setShowNewTagForm(true)}
                      className="w-full px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl text-sm text-gray-500 dark:text-gray-400 hover:border-blue-500 hover:text-blue-600 transition-colors flex items-center justify-center gap-2">
                      <Plus size={16} /> Create New Tag
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* ═══════════════════ FIELDS TAB ═══════════════════ */}
            {activeTab === 'fields' && (
              <div>
                <div className="flex items-center justify-between mb-5">
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Fields ({fieldCount})
                  </span>
                  <button type="button" onClick={addField}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors">
                    <Plus size={16} /> Add Field
                  </button>
                </div>

                {fieldCount === 0 ? (
                  <div className="text-center py-16 text-gray-400">
                    <p className="text-sm mb-4">No fields yet.</p>
                    <button type="button" onClick={addField}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
                      Add First Field
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {(localData.fields || []).map((field, index) => {
                      const isPartitionLocked = partitionLockedColumnNames.includes(field.name);
                      return (
                        <div
                          key={index}
                          draggable={!isPartitionLocked}
                          onDragStart={e => handleDragStart(e, index)}
                          onDragOver={e => handleDragOver(e, index)}
                          onDragLeave={handleDragLeave}
                          onDrop={e => handleDrop(e, index)}
                          onDragEnd={handleDragEnd}
                          className={`p-4 rounded-xl border bg-gray-50 dark:bg-gray-700/50 transition-all ${
                            draggedFieldIndex === index ? 'opacity-50' : ''
                          } ${dragOverFieldIndex === index
                            ? 'border-blue-500 border-2'
                            : isPartitionLocked
                              ? 'border-blue-300 dark:border-blue-700/80 bg-blue-50/30 dark:bg-blue-950/20'
                              : 'border-gray-200 dark:border-gray-600'
                          }`}
                        >
                          {/* Partition Lock Notice */}
                          {isPartitionLocked && (
                            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-100/70 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800 rounded-lg text-xs text-blue-800 dark:text-blue-300 mb-3 font-medium">
                              <Lock size={13} className="text-blue-600 dark:text-blue-400 flex-shrink-0" />
                              <span>This field is locked as an active <strong>{partitioning?.type?.toUpperCase()}</strong> partition key. Remove or change the partition in the <em>Partitions</em> tab to edit or delete this field.</span>
                            </div>
                          )}

                          {/* Row 1: grip + name + lock badge */}
                          <div className="flex items-center gap-3 mb-3">
                            <div className={`text-gray-400 ${isPartitionLocked ? 'cursor-not-allowed opacity-40' : 'cursor-move hover:text-gray-600 dark:hover:text-gray-300'}`} title={isPartitionLocked ? "Reorder disabled for locked partition field" : "Drag to reorder"}>
                              <GripVertical size={18} />
                            </div>
                            <input
                              ref={index === (localData.fields || []).length - 1 && shouldFocusNewField ? newFieldInputRef : null}
                              type="text"
                              value={field.name || ''}
                              disabled={isPartitionLocked}
                              onChange={e => updateField(index, { ...field, name: e.target.value })}
                              placeholder="Field name"
                              title={isPartitionLocked ? "Field locked: used as active partition key" : undefined}
                              className={`flex-1 px-3 py-2 border rounded-lg text-sm transition-colors ${
                                isPartitionLocked
                                  ? 'border-blue-300 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-950/30 text-blue-950 dark:text-blue-200 font-semibold cursor-not-allowed'
                                  : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
                              }`}
                            />
                            {isPartitionLocked && (
                              <span className="flex items-center gap-1 text-[11px] px-2.5 py-1 bg-blue-100 dark:bg-blue-900/60 text-blue-700 dark:text-blue-300 font-semibold rounded-md flex-shrink-0" title="Partition Key Locked">
                                <Lock size={12} /> PART KEY
                              </span>
                            )}
                          </div>

                          {/* Row 2: type */}
                          <select
                            value={field.type || ''}
                            disabled={isPartitionLocked}
                            onChange={e => updateField(index, { ...field, type: e.target.value })}
                            title={isPartitionLocked ? "Data type locked: used as active partition key" : undefined}
                            className={`w-full px-3 py-2 border rounded-lg text-sm mb-3 transition-colors ${
                              isPartitionLocked
                                ? 'border-blue-300 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-950/30 text-gray-700 dark:text-gray-300 cursor-not-allowed'
                                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
                            }`}
                          >
                            {getDataTypes().map(t => <option key={t} value={t}>{t}</option>)}
                          </select>

                          {/* Row 3: description */}
                          <input
                            type="text"
                            value={field.description || ''}
                            onChange={e => updateField(index, { ...field, description: e.target.value })}
                            placeholder="Field description (optional)"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm mb-2"
                          />

                          {/* CHECK Constraint */}
                          <input
                            type="text"
                            value={field.checkConstraint || ''}
                            onChange={e => updateField(index, { ...field, checkConstraint: e.target.value })}
                            placeholder="CHECK Constraint (e.g. age >= 18 or status IN ('A','M'))"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-mono mb-3"
                          />

                          {/* Row 4: checkboxes + actions */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4 text-sm text-gray-700 dark:text-gray-300">
                              <label className="flex items-center gap-1.5 cursor-pointer">
                                <input type="checkbox" checked={field.isPrimaryKey || false}
                                  onChange={e => updateField(index, { ...field, isPrimaryKey: e.target.checked, nullable: e.target.checked ? false : field.nullable })}
                                  className="rounded" />
                                <Key size={13} className="text-yellow-500" /> PK
                              </label>
                              <label className="flex items-center gap-1.5 cursor-pointer">
                                <input type="checkbox" checked={field.autoIncrement || false}
                                  onChange={e => updateField(index, { ...field, autoIncrement: e.target.checked })}
                                  className="rounded" />
                                <Hash size={13} className="text-blue-500" /> Auto
                              </label>
                              <label className="flex items-center gap-1.5 cursor-pointer">
                                <input type="checkbox" checked={field.nullable !== false}
                                  onChange={e => updateField(index, { ...field, nullable: e.target.checked })}
                                  disabled={field.isPrimaryKey}
                                  className="rounded disabled:opacity-50" />
                                <span className={field.isPrimaryKey ? 'opacity-50' : ''}>Nullable</span>
                              </label>
                            </div>

                            <div className="flex items-center gap-1">
                              <button onClick={() => handleSaveDomainField(field)} disabled={!userHasRole}
                                className={`p-2 rounded-lg transition-colors ${userHasRole ? 'text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20' : 'text-gray-400 opacity-50 cursor-not-allowed'}`}
                                title={userHasRole ? 'Save field as object' : 'Subscription required'}>
                                <Library size={16} />
                              </button>
                              <button
                                type="button"
                                onClick={() => deleteField(index)}
                                disabled={isPartitionLocked}
                                className={`p-2 rounded-lg transition-colors ${
                                  isPartitionLocked
                                    ? 'text-gray-400 opacity-40 cursor-not-allowed'
                                    : 'text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20'
                                }`}
                                title={isPartitionLocked ? "Cannot delete: field is locked by active partitioning" : "Delete field"}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ═══════════════════ RELATIONSHIPS TAB ═══════════════════ */}
            {activeTab === 'relationships' && (
              <div className="space-y-6">
                {relCount === 0 ? (
                  <div className="text-center py-16 text-gray-400 text-sm">
                    No relationships connected to this entity.
                  </div>
                ) : (
                  <>
                    {outgoing.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <ArrowRight size={16} className="text-blue-600 dark:text-blue-400" />
                          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                            Outgoing ({outgoing.length})
                          </h3>
                        </div>
                        <div className="space-y-2">
                          {outgoing.map(rel => (
                            <button key={rel.id} onClick={() => { onSelectEdge(rel); onClose(); }}
                              className="w-full p-4 rounded-xl border border-gray-200 dark:border-gray-600 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all text-left">
                              <div className="flex items-center gap-2 mb-1">
                                <Link2 size={13} className="text-blue-600 dark:text-blue-400" />
                                <span className="text-sm font-medium text-gray-900 dark:text-white">{rel.data?.label || 'Unnamed'}</span>
                                <span className="ml-auto text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full">
                                  {rel.data?.cardinality || 'N/A'}
                                </span>
                              </div>
                              <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                                <ArrowRight size={11} />
                                <span>{rel.targetNode?.data?.name || 'Unknown'}</span>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {incoming.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <ArrowLeft size={16} className="text-green-600 dark:text-green-400" />
                          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                            Incoming ({incoming.length})
                          </h3>
                        </div>
                        <div className="space-y-2">
                          {incoming.map(rel => {
                            const fkName = (rel.data?.fkAlias && String(rel.data.fkAlias).trim()) || `${rel.sourceNode?.data?.name?.toLowerCase()}_id`;
                            const isFkPartitionLocked = partitionLockedColumnNames.some(col => col.toLowerCase() === fkName.toLowerCase());
                            return (
                              <button key={rel.id} onClick={() => { onSelectEdge(rel); onClose(); }}
                                className={`w-full p-4 rounded-xl border transition-all text-left ${
                                  isFkPartitionLocked
                                    ? 'border-blue-300 dark:border-blue-700/80 bg-blue-50/30 dark:bg-blue-950/20'
                                    : 'border-gray-200 dark:border-gray-600 hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-900/20'
                                }`}>
                                <div className="flex items-center gap-2 mb-1">
                                  <Link2 size={13} className="text-green-600 dark:text-green-400" />
                                  <span className="text-sm font-medium text-gray-900 dark:text-white">{rel.data?.label || 'Unnamed'}</span>
                                  <span className="ml-auto text-xs px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full">
                                    {rel.data?.cardinality || 'N/A'}
                                  </span>
                                  {isFkPartitionLocked && (
                                    <span className="flex items-center gap-1 text-[11px] px-2 py-0.5 bg-blue-100 dark:bg-blue-900/60 text-blue-700 dark:text-blue-300 font-semibold rounded-md flex-shrink-0" title="This Foreign Key is locked as an active partition key">
                                      <Lock size={11} /> PART KEY
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                                  <ArrowLeft size={11} />
                                  <span>{rel.sourceNode?.data?.name || 'Unknown'}</span>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <p className="text-xs text-gray-400 dark:text-gray-500 text-center pt-2">
                      Click a relationship to view and edit its properties
                    </p>
                  </>
                )}
              </div>
            )}
            {/* ════════════════════ PARTITIONS TAB ════════════════════ */}
            {activeTab === 'partitions' && (
              <div className="space-y-6">
                {/* Partition Type Selector */}
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                    Partitioning Strategy
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[
                      { id: 'none', label: 'None', desc: 'No partitioning', icon: Database },
                      { id: 'range', label: 'Range', desc: 'Intervals (from/to)', icon: ChevronRight },
                      { id: 'list', label: 'List', desc: 'Discrete values list', icon: Split },
                      { id: 'hash', label: 'Hash', desc: 'Modulo distribution', icon: Hash },
                    ].map(strat => {
                      const isSelected = currentPartitioning.type === strat.id;
                      const Icon = strat.icon;
                      return (
                        <button
                          key={strat.id}
                          type="button"
                          onClick={() => handleSetPartitionType(strat.id)}
                          className={`flex flex-col items-start p-3 rounded-xl border text-left transition-all ${
                            isSelected
                              ? 'border-blue-500 bg-blue-50/70 dark:bg-blue-900/30 text-blue-900 dark:text-blue-200 ring-2 ring-blue-500/20 shadow-sm'
                              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          <div className="flex items-center gap-1.5 mb-1">
                            <Icon size={14} className={isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'} />
                            <span className="font-semibold text-sm">{strat.label}</span>
                          </div>
                          <span className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">
                            {strat.desc}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* ── NONE ── */}
                {currentPartitioning.type === 'none' && (
                  <div className="text-center py-10 px-4 bg-gray-50 dark:bg-gray-800/40 border border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
                    <Layers size={32} className="mx-auto text-gray-400 mb-2" />
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      No partitioning configured
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-md mx-auto">
                      Select Range, List, or Hash above to divide large table datasets into performant partition segments.
                    </p>
                  </div>
                )}

                {/* ── RANGE PARTITIONING ── */}
                {currentPartitioning.type === 'range' && (
                  <div className="space-y-5">
                    {/* Range Column Selector */}
                    <div className="p-4 bg-blue-50/50 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900/40 rounded-xl">
                      <label className="block text-xs font-semibold text-blue-900 dark:text-blue-300 mb-1">
                        Partition Column (Range Key) <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={currentPartitioning.column || ''}
                        onChange={(e) => handleSetPartitionColumn(e.target.value)}
                        className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                      >
                        <option value="" disabled>Select a column or foreign key...</option>
                        {getAllEligiblePartitionFields().map(f => (
                          <option key={f.name} value={f.name}>
                            {f.name} ({f.type}){f.isForeignKey ? ' [FK]' : ''}{f.isPrimaryKey ? ' [PK]' : ''}
                          </option>
                        ))}
                      </select>
                      <p className="text-[11px] text-blue-700/80 dark:text-blue-400/80 mt-1.5 flex items-center gap-1">
                        <Info size={12} />
                        Values for this column (including foreign keys) will be partitioned across the defined intervals.
                      </p>
                    </div>

                    {/* Add / Edit Range Partition Form */}
                    <div className="p-4 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-xl space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                          <Layers size={13} className="text-blue-600 dark:text-blue-400" />
                          {isEditingPartition ? 'Edit Range Partition' : 'Add Range Partition'}
                        </span>
                        {isEditingPartition && (
                          <button
                            type="button"
                            onClick={handleCancelPartitionEdit}
                            className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                          >
                            Cancel Edit
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-[11px] font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Partition Name
                          </label>
                          <input
                            type="text"
                            placeholder="e.g. p_2024, p0"
                            value={partitionForm.name}
                            onChange={(e) => setPartitionForm({ ...partitionForm, name: e.target.value })}
                            className="w-full px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-xs text-gray-900 dark:text-white outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Start Interval (From)
                          </label>
                          <input
                            type="text"
                            placeholder="e.g. 2024-01-01 or 1"
                            value={partitionForm.from}
                            onChange={(e) => setPartitionForm({ ...partitionForm, from: e.target.value })}
                            className="w-full px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-xs text-gray-900 dark:text-white outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-medium text-gray-600 dark:text-gray-400 mb-1">
                            End Interval (To / Less Than)
                          </label>
                          <input
                            type="text"
                            placeholder="e.g. 2025-01-01 or 100"
                            value={partitionForm.to}
                            onChange={(e) => setPartitionForm({ ...partitionForm, to: e.target.value })}
                            className="w-full px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-xs text-gray-900 dark:text-white outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-1">
                        <p className="text-[11px] text-gray-500 dark:text-gray-400">
                          * At least Start or End interval must be specified.
                        </p>
                        <button
                          type="button"
                          onClick={handleSaveRangePartition}
                          className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors shadow-sm"
                        >
                          {isEditingPartition ? <Check size={13} /> : <Plus size={13} />}
                          {isEditingPartition ? 'Update Partition' : 'Add Partition'}
                        </button>
                      </div>
                    </div>

                    {/* Range Partitions List Table */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                          Defined Range Partitions ({(currentPartitioning.partitions || []).length})
                        </span>
                      </div>

                      {(currentPartitioning.partitions || []).length === 0 ? (
                        <div className="text-center py-6 border border-dashed border-gray-200 dark:border-gray-700 rounded-xl text-gray-400 text-xs">
                          No intervals defined yet. Use the form above to add partition ranges.
                        </div>
                      ) : (
                        <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm">
                          <table className="w-full text-left text-xs">
                            <thead className="bg-gray-50 dark:bg-gray-800/80 border-b border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300">
                              <tr>
                                <th className="px-3 py-2.5 font-semibold">Partition Name</th>
                                <th className="px-3 py-2.5 font-semibold">Start (From)</th>
                                <th className="px-3 py-2.5 font-semibold">End (To)</th>
                                <th className="px-3 py-2.5 font-semibold">Range Expression</th>
                                <th className="px-3 py-2.5 font-semibold text-right">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                              {(currentPartitioning.partitions || []).map((part) => {
                                const fromText = part.from || 'MINVALUE';
                                const toText = part.to || 'MAXVALUE';
                                return (
                                  <tr key={part.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                    <td className="px-3 py-2 font-medium text-gray-900 dark:text-white">
                                      {part.name}
                                    </td>
                                    <td className="px-3 py-2 text-gray-600 dark:text-gray-300 font-mono text-[11px]">
                                      {part.from || <span className="text-gray-400 italic">MINVALUE</span>}
                                    </td>
                                    <td className="px-3 py-2 text-gray-600 dark:text-gray-300 font-mono text-[11px]">
                                      {part.to || <span className="text-gray-400 italic">MAXVALUE</span>}
                                    </td>
                                    <td className="px-3 py-2 text-blue-600 dark:text-blue-400 font-mono text-[11px]">
                                      {part.from && part.to
                                        ? `FROM ('${part.from}') TO ('${part.to}')`
                                        : part.to
                                          ? `LESS THAN ('${part.to}')`
                                          : `FROM ('${part.from}') TO (MAXVALUE)`
                                      }
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                      <div className="flex items-center justify-end gap-1">
                                        <button
                                          type="button"
                                          onClick={() => handleEditPartition(part)}
                                          className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                          title="Edit partition"
                                        >
                                          <Edit2 size={13} />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleDeletePartition(part.id)}
                                          className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded text-gray-500 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                                          title="Delete partition"
                                        >
                                          <Trash2 size={13} />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ── LIST PARTITIONING ── */}
                {currentPartitioning.type === 'list' && (
                  <div className="space-y-5">
                    {/* List Column Selector */}
                    <div className="p-4 bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900/40 rounded-xl">
                      <label className="block text-xs font-semibold text-emerald-900 dark:text-emerald-300 mb-1">
                        Partition Column (List Key) <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={currentPartitioning.column || ''}
                        onChange={(e) => handleSetPartitionColumn(e.target.value)}
                        className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-emerald-200 dark:border-emerald-800 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 outline-none"
                      >
                        <option value="" disabled>Select a column or foreign key...</option>
                        {getAllEligiblePartitionFields().map(f => (
                          <option key={f.name} value={f.name}>
                            {f.name} ({f.type}){f.isForeignKey ? ' [FK]' : ''}{f.isPrimaryKey ? ' [PK]' : ''}
                          </option>
                        ))}
                      </select>
                      <p className="text-[11px] text-emerald-700/80 dark:text-emerald-400/80 mt-1.5 flex items-center gap-1">
                        <Info size={12} />
                        Row values in this column (including foreign keys) will be matched against discrete lists of values.
                      </p>
                    </div>

                    {/* Add / Edit List Partition Form */}
                    <div className="p-4 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-xl space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                          <Split size={13} className="text-emerald-600 dark:text-emerald-400" />
                          {isEditingPartition ? 'Edit List Partition' : 'Add List Partition'}
                        </span>
                        {isEditingPartition && (
                          <button
                            type="button"
                            onClick={handleCancelPartitionEdit}
                            className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                          >
                            Cancel Edit
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[11px] font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Partition Name
                          </label>
                          <input
                            type="text"
                            placeholder="e.g. p_sudeste, p_norte"
                            value={partitionForm.name}
                            onChange={(e) => setPartitionForm({ ...partitionForm, name: e.target.value })}
                            className="w-full px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-xs text-gray-900 dark:text-white outline-none focus:ring-1 focus:ring-emerald-500"
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Values List (comma-separated, optional)
                          </label>
                          <input
                            type="text"
                            placeholder="e.g. 'SP', 'RJ', 'MG', 'ES' or 1, 2, 3"
                            value={partitionForm.values}
                            onChange={(e) => setPartitionForm({ ...partitionForm, values: e.target.value })}
                            className="w-full px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-xs text-gray-900 dark:text-white outline-none focus:ring-1 focus:ring-emerald-500"
                          />
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-1">
                        <p className="text-[11px] text-gray-500 dark:text-gray-400">
                          Values can be comma-separated strings, numbers or codes.
                        </p>
                        <button
                          type="button"
                          onClick={handleSaveListPartition}
                          className="flex items-center gap-1.5 px-4 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 transition-colors shadow-sm"
                        >
                          {isEditingPartition ? <Check size={13} /> : <Plus size={13} />}
                          {isEditingPartition ? 'Update Partition' : 'Add Partition'}
                        </button>
                      </div>
                    </div>

                    {/* List Partitions Table */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                          Defined List Partitions ({(currentPartitioning.partitions || []).length})
                        </span>
                      </div>

                      {(currentPartitioning.partitions || []).length === 0 ? (
                        <div className="text-center py-6 border border-dashed border-gray-200 dark:border-gray-700 rounded-xl text-gray-400 text-xs">
                          No list partitions defined yet. Use the form above to add partitions.
                        </div>
                      ) : (
                        <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm">
                          <table className="w-full text-left text-xs">
                            <thead className="bg-gray-50 dark:bg-gray-800/80 border-b border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300">
                              <tr>
                                <th className="px-3 py-2.5 font-semibold">Partition Name</th>
                                <th className="px-3 py-2.5 font-semibold">Values (IN)</th>
                                <th className="px-3 py-2.5 font-semibold text-right">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                              {(currentPartitioning.partitions || []).map((part) => (
                                <tr key={part.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                  <td className="px-3 py-2 font-medium text-gray-900 dark:text-white">
                                    {part.name}
                                  </td>
                                  <td className="px-3 py-2 text-emerald-600 dark:text-emerald-400 font-mono text-[11px]">
                                    {part.values ? `VALUES (${part.values})` : <span className="text-gray-400 italic">DEFAULT / ANY</span>}
                                  </td>
                                  <td className="px-3 py-2 text-right">
                                    <div className="flex items-center justify-end gap-1">
                                      <button
                                        type="button"
                                        onClick={() => handleEditPartition(part)}
                                        className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded text-gray-500 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                                        title="Edit partition"
                                      >
                                        <Edit2 size={13} />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleDeletePartition(part.id)}
                                        className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded text-gray-500 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                                        title="Delete partition"
                                      >
                                        <Trash2 size={13} />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ── HASH PARTITIONING ── */}
                {currentPartitioning.type === 'hash' && (
                  <div className="space-y-5">
                    {/* Hash Columns Multi-Select */}
                    <div className="p-4 bg-purple-50/50 dark:bg-purple-950/20 border border-purple-100 dark:border-purple-900/40 rounded-xl space-y-3">
                      <div className="flex items-center justify-between">
                        <label className="block text-xs font-semibold text-purple-900 dark:text-purple-300">
                          Hash Key Columns (at least 1 required) <span className="text-red-500">*</span>
                        </label>
                        <span className="text-[11px] px-2 py-0.5 bg-purple-200/70 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300 font-semibold rounded-full">
                          {(currentPartitioning.columns || []).length} selected
                        </span>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto p-1">
                        {getAllEligiblePartitionFields().map(f => {
                          const isChecked = (currentPartitioning.columns || []).includes(f.name);
                          return (
                            <label
                              key={f.name}
                              className={`flex items-center gap-2 p-2 rounded-lg border text-xs cursor-pointer transition-all ${
                                isChecked
                                  ? 'border-purple-500 bg-purple-100/50 dark:bg-purple-900/40 text-purple-900 dark:text-purple-200 font-medium'
                                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => handleToggleHashColumn(f.name)}
                                className="rounded text-purple-600 focus:ring-purple-500"
                              />
                              <div className="flex items-center gap-1 truncate min-w-0">
                                {f.isForeignKey && <Link2 size={11} className="text-purple-500 flex-shrink-0" />}
                                {f.isPrimaryKey && <Key size={11} className="text-yellow-500 flex-shrink-0" />}
                                <span className="truncate">{f.name}</span>
                              </div>
                            </label>
                          );
                        })}
                      </div>

                      {(currentPartitioning.columns || []).length === 0 && (
                        <p className="text-[11px] text-amber-600 dark:text-amber-400 flex items-center gap-1">
                          <AlertCircle size={12} />
                          Please select at least one column for hash partitioning.
                        </p>
                      )}
                    </div>

                    {/* Partitions Count */}
                    <div className="p-4 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-xl space-y-3">
                      <label className="block text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                        Number of Hash Partitions
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="number"
                          min="1"
                          max="256"
                          value={currentPartitioning.hashPartitionsCount || 4}
                          onChange={(e) => handleSetHashPartitionsCount(e.target.value)}
                          className="w-28 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-purple-500"
                        />
                        <div className="flex items-center gap-1.5">
                          {[2, 4, 8, 16, 32].map(num => (
                            <button
                              key={num}
                              type="button"
                              onClick={() => handleSetHashPartitionsCount(num)}
                              className={`px-2.5 py-1 text-xs rounded-md border font-mono transition-colors ${
                                currentPartitioning.hashPartitionsCount === num
                                  ? 'bg-purple-600 text-white border-purple-600'
                                  : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                              }`}
                            >
                              {num}
                            </button>
                          ))}
                        </div>
                      </div>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400">
                        Data will be distributed evenly across {currentPartitioning.hashPartitionsCount || 4} hash buckets using the selected key column(s).
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Footer ── */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0 bg-gray-50 dark:bg-gray-800/50 rounded-b-xl">
            <button
              onClick={onClose}
              className="px-6 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm"
            >
              <Save size={16} /> Save Changes
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmOpen}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText={confirmConfig.confirmText}
        cancelText={confirmConfig.cancelText}
        type={confirmConfig.type}
        onConfirm={confirmConfig.onConfirm}
        onCancel={cancel}
      />

      {showSaveDomainModal && (
        <SaveDomainModal
          onClose={() => { setShowSaveDomainModal(false); setSaveDomainData(null); }}
          onSave={handleConfirmSaveDomain}
          entityData={saveDomainData?.entity}
          fieldData={saveDomainData?.field}
          modelType={modelType}
        />
      )}
    </>
  );
};

export default EntityEditModal;
